import bloomWGSL from "./shaders/bloom.wgsl?raw";

const LEVELS = 6;
const PARAMS_BYTES = 16;
const HDR_FORMAT: GPUTextureFormat = "rgba16float";

export class BloomPass {
  private device: GPUDevice;
  private layout: GPUBindGroupLayout;
  private sampler: GPUSampler;
  private prefilterPipeline: GPURenderPipeline;
  private downPipeline: GPURenderPipeline;
  private upPipeline: GPURenderPipeline;

  private mips: GPUTexture[] = [];
  private mipViews: GPUTextureView[] = [];
  private mipSizes: Array<{ w: number; h: number }> = [];

  // One UBO per pass: writeBuffer queues before submit, so a shared buffer
  // would only carry the final pass's params.
  private prefilterUBO!: GPUBuffer;
  private downUBOs: GPUBuffer[] = [];
  private upUBOs: GPUBuffer[] = [];

  private prefilterBind: GPUBindGroup | null = null;
  private downBinds: GPUBindGroup[] = [];
  private upBinds: GPUBindGroup[] = [];

  private srcW = 0;
  private srcH = 0;

  private scratch = new Float32Array(4);

  constructor(device: GPUDevice) {
    this.device = device;

    this.layout = device.createBindGroupLayout({
      label: "bloom bgl",
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
      ],
    });

    this.sampler = device.createSampler({
      label: "bloom sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });

    const shader = device.createShaderModule({ label: "bloom", code: bloomWGSL });
    const pipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [this.layout] });

    this.prefilterPipeline = device.createRenderPipeline({
      label: "bloom prefilter",
      layout: pipelineLayout,
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_prefilter",
        targets: [{ format: HDR_FORMAT }],
      },
      primitive: { topology: "triangle-list" },
    });

    this.downPipeline = device.createRenderPipeline({
      label: "bloom down",
      layout: pipelineLayout,
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_down",
        targets: [{ format: HDR_FORMAT }],
      },
      primitive: { topology: "triangle-list" },
    });

    // Upsample additively blends onto the loaded downsample mip.
    this.upPipeline = device.createRenderPipeline({
      label: "bloom up",
      layout: pipelineLayout,
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_up",
        targets: [{
          format: HDR_FORMAT,
          blend: {
            color: { srcFactor: "one", dstFactor: "one", operation: "add" },
            alpha: { srcFactor: "one", dstFactor: "one", operation: "add" },
          },
        }],
      },
      primitive: { topology: "triangle-list" },
    });

    this.prefilterUBO = this.makeUBO("bloom prefilter ubo");
    for (let i = 0; i < LEVELS - 1; i++) {
      this.downUBOs.push(this.makeUBO(`bloom down ubo ${i}`));
      this.upUBOs.push(this.makeUBO(`bloom up ubo ${i}`));
    }
  }

  private makeUBO(label: string): GPUBuffer {
    return this.device.createBuffer({
      label,
      size: PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  /** Call after every HDR recreate; hdrView is captured into prefilter bind. */
  resize(srcW: number, srcH: number, hdrView: GPUTextureView): void {
    this.srcW = srcW;
    this.srcH = srcH;

    for (const t of this.mips) t.destroy();
    this.mips = [];
    this.mipViews = [];
    this.mipSizes = [];

    for (let i = 0; i < LEVELS; i++) {
      const w = Math.max(1, srcW >> (i + 1));
      const h = Math.max(1, srcH >> (i + 1));
      const tex = this.device.createTexture({
        label: `bloom mip ${i}`,
        size: { width: w, height: h },
        format: HDR_FORMAT,
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      });
      this.mips.push(tex);
      this.mipViews.push(tex.createView());
      this.mipSizes.push({ w, h });
    }

    this.prefilterBind = this.device.createBindGroup({
      label: "bloom prefilter bind",
      layout: this.layout,
      entries: [
        { binding: 0, resource: hdrView },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: { buffer: this.prefilterUBO } },
      ],
    });

    this.downBinds = [];
    this.upBinds = [];
    for (let i = 0; i < LEVELS - 1; i++) {
      this.downBinds.push(this.device.createBindGroup({
        label: `bloom down bind ${i}`,
        layout: this.layout,
        entries: [
          { binding: 0, resource: this.mipViews[i] },
          { binding: 1, resource: this.sampler },
          { binding: 2, resource: { buffer: this.downUBOs[i] } },
        ],
      }));
      this.upBinds.push(this.device.createBindGroup({
        label: `bloom up bind ${i}`,
        layout: this.layout,
        entries: [
          { binding: 0, resource: this.mipViews[i + 1] },
          { binding: 1, resource: this.sampler },
          { binding: 2, resource: { buffer: this.upUBOs[i] } },
        ],
      }));
    }
  }

  /** mip 0 = half HDR; tonemap bilinear-upscales 2×. */
  bloomView(): GPUTextureView {
    return this.mipViews[0];
  }

  encode(enc: GPUCommandEncoder, threshold: number, scatter: number): void {
    if (!this.prefilterBind) return;

    const q = this.device.queue;
    const s = this.scratch;

    s[0] = 1.0 / this.srcW;
    s[1] = 1.0 / this.srcH;
    s[2] = Math.max(threshold, 0);
    s[3] = 0;
    q.writeBuffer(this.prefilterUBO, 0, s.buffer, 0, PARAMS_BYTES);

    for (let i = 0; i < LEVELS - 1; i++) {
      const src = this.mipSizes[i];
      s[0] = 1.0 / src.w;
      s[1] = 1.0 / src.h;
      s[2] = 0;
      s[3] = 0;
      q.writeBuffer(this.downUBOs[i], 0, s.buffer, 0, PARAMS_BYTES);

      const upSrc = this.mipSizes[i + 1];
      s[0] = 1.0 / upSrc.w;
      s[1] = 1.0 / upSrc.h;
      s[2] = scatter;
      s[3] = 0;
      q.writeBuffer(this.upUBOs[i], 0, s.buffer, 0, PARAMS_BYTES);
    }

    {
      const pass = enc.beginRenderPass({
        label: "bloom prefilter",
        colorAttachments: [{
          view: this.mipViews[0],
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
        }],
      });
      pass.setPipeline(this.prefilterPipeline);
      pass.setBindGroup(0, this.prefilterBind);
      pass.draw(3);
      pass.end();
    }

    for (let i = 0; i < LEVELS - 1; i++) {
      const pass = enc.beginRenderPass({
        label: `bloom down ${i}`,
        colorAttachments: [{
          view: this.mipViews[i + 1],
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
        }],
      });
      pass.setPipeline(this.downPipeline);
      pass.setBindGroup(0, this.downBinds[i]);
      pass.draw(3);
      pass.end();
    }

    // loadOp "load" so the additive blend accumulates onto the downsample.
    for (let i = LEVELS - 2; i >= 0; i--) {
      const pass = enc.beginRenderPass({
        label: `bloom up ${i}`,
        colorAttachments: [{
          view: this.mipViews[i],
          loadOp: "load",
          storeOp: "store",
        }],
      });
      pass.setPipeline(this.upPipeline);
      pass.setBindGroup(0, this.upBinds[i]);
      pass.draw(3);
      pass.end();
    }
  }
}
